import { Injectable, inject, isDevMode } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, catchError, switchMap, tap, from } from 'rxjs';
import { ENV } from '@bodyanalytics/app-config';
// Import directly from the source modules rather than the '@bodyanalytics/ui-core'
// barrel. This file is itself re-exported by that barrel (src/index.ts), so importing
// from the barrel creates a circular import-alias (TS2303/TS2459) that fails Jest
// compilation for every consumer of ui-core (e.g. all front/admin libs).
import { ApiLocalRoutesEnum } from '../../enums/api-routes.enum';
import { AdminAuthStorageKeys } from '../../enums/admin-auth-storage-keys.enum';
import { SecureStorageService } from '../encryption/secure-storage.service';
import { SecureAdminTenantInfo } from '../../interfaces/secure-admin-tenant-info.interface';
import { AdminUserInfo } from '@bodyanalytics/data-models-ui';
import { safeErrorSummary } from '../../helper/safe-error-summary';

/**
 * Outcome of the post-login identity check (#2070).
 *
 *  - `'ok'`        — the caller is a known admin and the tenant info was stored.
 *  - `'not-admin'` — a REAL access decision: the email is missing from the token,
 *                    not on the admin allowlist, or the backend answered
 *                    (successfully) that no admin exists for it.
 *  - `'error'`     — we could NOT determine the answer: the identity lookup
 *                    (`GET tenant/admin-by-email`) failed with 5xx/network, the
 *                    token fetch failed, or secure storage failed. Callers must
 *                    surface this as a retryable error, never as a denial —
 *                    collapsing it into `false` is exactly what produced the
 *                    silent bounce-to-login loop during the 2026-07-09 outage.
 */
export type AdminIdentityCheckStatus = 'ok' | 'not-admin' | 'error';

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private env = inject(ENV);
  private secureStorage = inject(SecureStorageService);

  // Admin emails are now configured via environment variables for security
  private get adminEmails(): string[] {
    return this.env.adminEmails || [];
  }

  /**
   * Dev-only logging. Suppressed in production builds so admin email lists,
   * decoded JWT claims, tenant info, and raw localStorage tokens are never
   * written to the browser console of a deployed app.
   */
  private debug(...args: unknown[]): void {
    if (isDevMode()) {
      console.warn(...args);
    }
  }

  /**
   * Checks if the current user is an admin and automatically stores tenant info.
   *
   * Boolean convenience wrapper over {@link resolveAdminIdentity} — kept for
   * callers that only need "did we end up with a stored admin identity?" and
   * must never receive an error (dashboard bootstrap, account details). It
   * cannot distinguish "not an admin" from "the lookup failed"; access-decision
   * call sites (AdminRoleGuard) must use {@link resolveAdminIdentity} instead.
   */
  checkAndStoreAdminTenantInfo(): Observable<boolean> {
    return this.resolveAdminIdentity().pipe(map(status => status === 'ok'));
  }

  /**
   * Resolves the post-login admin identity and stores the tenant info on success.
   *
   * Unlike the boolean wrapper, this keeps transport/storage FAILURES ('error')
   * distinct from a genuine access decision ('not-admin') — see
   * {@link AdminIdentityCheckStatus} and #2070. Never errors; failures are
   * folded into the 'error' status.
   */
  resolveAdminIdentity(): Observable<AdminIdentityCheckStatus> {
    this.debug('AdminAuthService: Starting resolveAdminIdentity');
    // #2249: count only — never print the allowlist itself, even in dev builds.
    this.debug('AdminAuthService: Configured admin emails (count):', this.adminEmails.length);

    return this.authService.getAccessTokenSilently().pipe(
      switchMap((token: string) => {
        try {
          this.debug('AdminAuthService: Got access token, parsing...');
          // Extract email from JWT token
          const payload = JSON.parse(atob(token.split('.')[1]));
          const email = payload.email || payload.custom_email_claim;

          this.debug('AdminAuthService: Email claim in token:', email ? '[present]' : '[absent]');
          this.debug('AdminAuthService: Is admin email check:', this.isAdminEmail(email || ''));

          if (!email || !this.isAdminEmail(email)) {
            this.debug('AdminAuthService: Not an admin email or email not found');
            return of<AdminIdentityCheckStatus>('not-admin');
          }

          this.debug('AdminAuthService: Fetching admin user details');
          // Fetch admin user details from backend
          return this.getAdminUserDetails(email).pipe(
            switchMap(userInfo => {
              this.debug('AdminAuthService: Received user info from backend:', userInfo ? '[present]' : '[none]');

              if (userInfo) {
                // Store consolidated tenant information encrypted in localStorage
                const adminTenantInfo: SecureAdminTenantInfo = {
                  tenantId: userInfo.tenantId,
                  userId: userInfo.id,
                  userEmail: userInfo.emailAddress,
                  userRole: userInfo.role,
                  storedAt: Date.now(),
                };

                // The TenantInterceptor attaches the X-Tenant-ID header by reading
                // the PLAIN 'tenantId' localStorage key — it runs synchronously on
                // every request and cannot await the encrypted store. The sensitive
                // fields (userId/email/role) stay encrypted under ADMIN_TENANT;
                // tenantId is not a secret (it is sent as a request header anyway),
                // so mirror just it into the plain key the interceptor reads. Without
                // this, no admin request carries X-Tenant-ID and the API's
                // TenantContextGuard 403s every tenant-scoped call (e.g. the events
                // list fails with "Unable to load testing events"). The matching
                // removeItem('tenantId') in clearAdminTenantInfo() keeps logout symmetric.
                if (userInfo.tenantId) {
                  localStorage.setItem('tenantId', userInfo.tenantId);
                }

                this.debug(
                  'AdminAuthService: Attempting to store admin tenant info (fields):',
                  Object.keys(adminTenantInfo),
                );

                return from(this.secureStorage.setSecureItem(AdminAuthStorageKeys.ADMIN_TENANT, adminTenantInfo)).pipe(
                  tap(() => {
                    this.debug('AdminAuthService: Successfully stored admin tenant info');
                  }),
                  map((): AdminIdentityCheckStatus => 'ok'),
                  catchError(storageError => {
                    console.error('AdminAuthService: Error storing to secure storage:', safeErrorSummary(storageError));
                    throw storageError;
                  }),
                );
              }
              // The backend answered successfully with no admin for this email —
              // a real access decision, not a failure.
              this.debug('AdminAuthService: No user info received from backend');
              return of<AdminIdentityCheckStatus>('not-admin');
            }),
            // HTTP failure (5xx/network) from the identity lookup, or a secure
            // storage failure: we could not DETERMINE the answer (#2070).
            catchError(error => {
              console.error('AdminAuthService: identity lookup/storage failed:', safeErrorSummary(error));
              return of<AdminIdentityCheckStatus>('error');
            }),
          );
        } catch (error) {
          console.error('AdminAuthService: Error parsing JWT token:', safeErrorSummary(error));
          return of<AdminIdentityCheckStatus>('error');
        }
      }),
      catchError(error => {
        console.error('AdminAuthService: Error getting access token:', safeErrorSummary(error));
        return of<AdminIdentityCheckStatus>('error');
      }),
    );
  }

  /**
   * Checks if an email is in the admin list
   */
  private isAdminEmail(email: string): boolean {
    return this.adminEmails.includes(email.toLowerCase());
  }

  /**
   * Fetches admin user details from the backend.
   *
   * The endpoint returns 200 with a `null` body for an unknown/non-admin email
   * (Prisma `findUnique` miss), so a null NEXT emission means "not an admin".
   * HTTP failures (5xx/network) are deliberately NOT swallowed into `null`
   * here — collapsing them made a dead backend indistinguishable from a
   * revoked account (#2070). They propagate to `resolveAdminIdentity`, which
   * classifies them as `'error'`.
   */
  private getAdminUserDetails(email: string): Observable<AdminUserInfo | null> {
    const url = `${this.env.api}/${ApiLocalRoutesEnum.API_SUFFIX_TENANT}/admin-by-email/${email}`;

    return this.http.get<AdminUserInfo | null>(url);
  }

  /**
   * Gets the complete admin tenant info
   */
  async getAdminTenantInfo(): Promise<SecureAdminTenantInfo | null> {
    try {
      return await this.secureStorage.getSecureItem<SecureAdminTenantInfo>(AdminAuthStorageKeys.ADMIN_TENANT);
    } catch (error) {
      console.error('AdminAuthService: Error retrieving admin tenant info:', safeErrorSummary(error));
      return null;
    }
  }

  /**
   * Gets the tenant ID for the current admin
   */
  async getTenantId(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.tenantId || null;
  }

  /**
   * Gets the user ID for the current admin
   */
  async getUserId(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userId || null;
  }

  /**
   * Gets the user email for the current admin
   */
  async getUserEmail(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userEmail || null;
  }

  /**
   * Gets the user role for the current admin
   */
  async getUserRole(): Promise<string | null> {
    const info = await this.getAdminTenantInfo();
    return info?.userRole || null;
  }

  /**
   * Checks if admin tenant info exists and is not expired
   */
  async hasValidAdminTenantInfo(maxAgeMs: number = 24 * 60 * 60 * 1000): Promise<boolean> {
    const info = await this.getAdminTenantInfo();
    if (!info) return false;

    const age = Date.now() - info.storedAt;
    return age < maxAgeMs;
  }

  /**
   * Debug method to check what's stored in localStorage (for development only)
   */
  async debugStoredInfo(): Promise<void> {
    this.debug('=== DEBUG: Admin Tenant Info ===');
    const info = await this.getAdminTenantInfo();
    this.debug('Stored admin tenant info:', info ? '[present]' : null);
    this.debug('Raw localStorage item exists:', !!localStorage.getItem(AdminAuthStorageKeys.ADMIN_TENANT));
    this.debug('Session key exists:', !!sessionStorage.getItem(AdminAuthStorageKeys.SESSION_KEY));
    this.debug('================================');
  }

  /**
   * Clears stored admin tenant info (for logout)
   */
  clearAdminTenantInfo(): void {
    this.secureStorage.removeSecureItem(AdminAuthStorageKeys.ADMIN_TENANT);
    this.secureStorage.clearSessionKey();

    // Also remove any legacy localStorage items for backward compatibility
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
  }
}
